import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import './styles/prototype-shared-css.css';
import './styles/themes.css';
import './App.css';

// Import Primary App Components
import {
  PrimaryAmbient,
  PrimaryHome,
  PrimaryCantTakeMedication,
  PrimaryCantFindMedication,
  PrimaryMedicationList,
  PrimaryHelp,
  PrimarySuccess
} from './components/PrimaryAppPrototype';

// Import Support App Components
import {
  SupportLanding,
  SupportHome,
  SupportAlertDetail,
  SupportTrends,
  SupportMeds,
  SupportMore
} from './components/SupportAppPrototype';

// Import Theme Components
import ModeToggle from './components/ModeToggle';

// Import Supabase services
import { medicationService, medicationLogService, realtimeService } from './services/supabaseService';
import { getCurrentDueDose, getNextDueDose, getDoseStatus } from './utils/doseTimeUtils';

// Determine user role from environment variable, URL parameter, or default to 'primary'
// In production, this would come from authentication
const getUserRole = () => {
  // Check URL parameter first (e.g., ?role=support)
  const urlParams = new URLSearchParams(window.location.search);
  const roleParam = urlParams.get('role');
  if (roleParam === 'support' || roleParam === 'primary') {
    return roleParam;
  }

  // Check environment variable (Create React App uses process.env with REACT_APP_ prefix)
  if (process.env.REACT_APP_USER_ROLE) {
    return process.env.REACT_APP_USER_ROLE;
  }

  // Default to primary
  return 'primary';
};

const App = () => {
  // Determine user role from URL param, env var, or default, and allow it to be changed via debug toggle
  const [userRole, setUserRole] = useState(() => getUserRole()); // 'primary' or 'support'
  const [primaryScreen, setPrimaryScreen] = useState('ambient');
  const [supportScreen, setSupportScreen] = useState('landing');
  const primaryTheme = 'light'; // Always use light mode
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('ready');
  const [transcript, setTranscript] = useState('');
  const [medications, setMedications] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(true);
  const [medicationsTaken, setMedicationsTaken] = useState([]); // Keep for backward compatibility
  const [medicationLogs, setMedicationLogs] = useState([]); // Store today's medication logs
  const [currentMedIndex, setCurrentMedIndex] = useState(0);
  const [showSync, setShowSync] = useState(false);
  const [lastSyncAction, setLastSyncAction] = useState('');
  
  // Debug time override state
  const [debugTimeOverride, setDebugTimeOverride] = useState(false);
  const debugTime = React.useMemo(() => {
    const date = new Date();
    date.setHours(11, 30, 0, 0);
    return date;
  }, []); // Create once, reuse the same Date object
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const medicationsChannelRef = useRef(null);  // Separate ref for medications subscription
  const medicationLogsChannelRef = useRef(null); // Separate ref for medication logs subscription

  const userName = 'Margaret';
  
  // Helper function to get current time (debug or real)
  const getCurrentTime = React.useCallback(() => {
    return debugTimeOverride ? debugTime : new Date();
  }, [debugTimeOverride, debugTime]);
  
  // Toggle debug time override
  const handleTimeOverrideToggle = React.useCallback(() => {
    setDebugTimeOverride(prev => !prev);
  }, []);

  // Define fetchMedications function that can be called from anywhere
  const fetchMedications = React.useCallback(async () => {
    try {
      setMedicationsLoading(true);
      const data = await medicationService.getAll();
      setMedications(data);
    } catch (error) {
      console.error('Failed to fetch medications:', error);
      // Fallback to empty array on error
      setMedications([]);
    } finally {
      setMedicationsLoading(false);
    }
  }, []);

  // Fetch today's medication logs
  const fetchTodayLogs = React.useCallback(async () => {
    try {
      const logs = await medicationLogService.getToday();
      setMedicationLogs(logs || []);
      
      // Also update medicationsTaken for backward compatibility
      const takenIds = [...new Set((logs || []).map(log => log.medication_id))];
      setMedicationsTaken(takenIds);
    } catch (error) {
      console.error('Failed to fetch medication logs:', error);
      setMedicationLogs([]);
    }
  }, []);

  // Fetch medications and logs on mount and set up real-time subscription
  useEffect(() => {
    // Initial fetch
    fetchMedications();
    if (userRole === 'primary') {
      fetchTodayLogs();
    }

    // Set up real-time subscription for medications
    const channel = realtimeService.subscribeToMedications((payload) => {
      console.log('Medication change detected:', payload);
      // Refresh medications when any change occurs (INSERT, UPDATE, DELETE)
      fetchMedications();
    });

    // Clean up any existing channel before setting new one
    if (medicationsChannelRef.current) {
      realtimeService.unsubscribe(medicationsChannelRef.current);
    }
    medicationsChannelRef.current = channel;

    // Cleanup on unmount
    return () => {
      if (channel) {
        realtimeService.unsubscribe(channel);
      }
    };
  }, [fetchMedications, userRole, fetchTodayLogs]);

  // Set up real-time subscription for medication logs (for both roles)
  useEffect(() => {
    const channel = realtimeService.subscribeToMedicationLogs((payload) => {
      console.log('Real-time update:', payload);
      if (payload.eventType === 'INSERT') {
        const loggedMedId = payload.new.medication_id;
        
        if (userRole === 'caregiver') {
          // Support: Show sync notification when medication is taken
          triggerSync('Medication taken (real-time)');
        } else if (userRole === 'primary') {
          // Primary: Update local state if medication was logged
          // This handles cases where a support person logs for them, or sync across devices
          // Guard check prevents duplicate updates if they already logged it locally
          setMedicationsTaken(prev => {
            if (prev.includes(loggedMedId)) {
              return prev; // Already logged, skip update
            }
            return [...prev, loggedMedId];
          });

          // Also update medicationLogs to include the new log entry
          setMedicationLogs(prev => {
            // Check if this log is already in the state (from optimistic update)
            const exists = prev.some(log => log.medication_id === payload.new.medication_id && log.dose_number === payload.new.dose_number);
            if (exists) {
              return prev; // Already have this log, skip
            }
            return [...prev, payload.new];
          });
        }
      }
    });

    // Clean up any existing channel before setting new one
    if (medicationLogsChannelRef.current) {
      realtimeService.unsubscribe(medicationLogsChannelRef.current);
    }
    medicationLogsChannelRef.current = channel;

    return () => {
      if (channel) {
        realtimeService.unsubscribe(channel);
      }
    };
  }, [userRole]);

  // Apply theme to document body (only for primary) - always light mode
  useEffect(() => {
    if (userRole === 'primary') {
      document.body.className = '';
    } else {
      document.body.className = '';
    }
    return () => {
      document.body.className = '';
    };
  }, [userRole]);

  // Define functions before useEffect that uses them
  const speak = useCallback((text) => {
    if (synthRef.current) {
      setVoiceStatus('speaking');
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.onend = () => setVoiceStatus('ready');
      synthRef.current.speak(utterance);
    }
  }, []);

  const triggerSync = useCallback((action) => {
    setShowSync(true);
    setLastSyncAction(action);
    setTimeout(() => setShowSync(false), 3000);
  }, []);

  // Reset today's medication logs (for testing)
  const resetTodayLogs = React.useCallback(async () => {
    try {
      await medicationLogService.deleteToday();
      setMedicationLogs([]);
      setMedicationsTaken([]);
      triggerSync('Doses reset');
    } catch (error) {
      console.error('Failed to reset today\'s medication logs:', error);
      triggerSync('Reset failed');
    }
  }, [triggerSync]);

  const handleMedicationTaken = useCallback(async (medicationId = null, doseNumber = null) => {
    // If medication and dose are provided, use them; otherwise fall back to currentMedIndex logic
    let currentMed;
    let nextDueDose;
    
    if (medicationId && doseNumber) {
      currentMed = medications.find(m => m.id === medicationId);
      nextDueDose = doseNumber;
    } else {
      currentMed = medications[currentMedIndex];
      if (!currentMed) return;
      // Determine which dose should be taken next (considering taken doses)
      nextDueDose = getNextDueDose(currentMed, medicationLogs, getCurrentTime());
    }
    
    if (!currentMed || nextDueDose === null) {
      console.warn('No dose available to take for medication:', currentMed?.name);
      return;
    }

    try {
      // Log medication taken to Supabase with dose number
      await medicationLogService.logTaken(currentMed.id, userName, nextDueDose);

      // Optimistic UI update
      const newLog = {
        id: `temp-${Date.now()}`,
        medication_id: currentMed.id,
        dose_number: nextDueDose,
        taken_at: new Date().toISOString(),
        user_name: userName,
        medications: {
          name: currentMed.name,
          dosage: currentMed.dosage
        }
      };
      setMedicationLogs(prev => [...prev, newLog]);
      
      // Also update medicationsTaken for backward compatibility
      const newTaken = [...medicationsTaken, currentMed.id];
      setMedicationsTaken(newTaken);

      // Find the next medication with a takeable dose
      const findNextDueMedication = () => {
        for (let i = 0; i < medications.length; i++) {
          const med = medications[i];
          if (!med.doses || med.doses.length === 0) continue;

          const doseStatuses = getDoseStatus(med, [...medicationLogs, newLog], getCurrentTime());
          const hasTakeableDoses = doseStatuses.some(status =>
            !status.isTaken && status.canTakeNow
          );

          if (hasTakeableDoses) {
            return i;
          }
        }
        return 0; // Default to first medication if none have takeable doses
      };

      const nextDueIndex = findNextDueMedication();
      setCurrentMedIndex(nextDueIndex);

      triggerSync('Medication taken');

      // Refresh logs to ensure consistency
      setTimeout(() => {
        fetchTodayLogs();
      }, 500);

      // Stay on home screen instead of navigating
      setPrimaryScreen('home');
    } catch (error) {
      console.error('Failed to log medication taken:', error);
      // Still update UI even if Supabase call fails
      const newTaken = [...medicationsTaken, currentMed.id];
      setMedicationsTaken(newTaken);
      triggerSync('Medication taken (offline)');
      
      setPrimaryScreen('home');
    }
  }, [medications, currentMedIndex, userName, medicationsTaken, medicationLogs, triggerSync, fetchTodayLogs]);

  const handleVoiceCommand = useCallback((command) => {
    setVoiceStatus('processing');
    setTimeout(() => {
      if (command.includes('took') || command.includes('yes')) {
        speak('Great job, Margaret!');
        handleMedicationTaken();
      } else if (command.includes('remind') || command.includes('later')) {
        speak('I will remind you in 10 minutes.');
        setPrimaryScreen('home');
      } else if (command.includes('help') || command.includes('find')) {
        speak('Let me help you.');
        setPrimaryScreen('cant-take');
      } else if (command.includes('show') && command.includes('med')) {
        speak('Here are your medications.');
        setPrimaryScreen('medication-list');
      } else if (command.includes('home')) {
        setPrimaryScreen('home');
      } else if (command.includes('hey') || command.includes('assistant')) {
        speak('Hello Margaret!');
        setPrimaryScreen('home');
      } else {
        speak('I did not catch that.');
        setVoiceStatus('ready');
      }
    }, 500);
  }, [speak, handleMedicationTaken]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      synthRef.current = window.speechSynthesis;
      
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;

        recognitionRef.current.onstart = () => {
          setVoiceStatus('listening');
          setIsListening(true);
        };

        recognitionRef.current.onresult = (event) => {
          const current = event.resultIndex;
          const transcriptText = event.results[current][0].transcript;
          setTranscript(transcriptText);
          if (event.results[current].isFinal) {
            handleVoiceCommand(transcriptText.toLowerCase());
          }
        };

        recognitionRef.current.onend = () => {
          setVoiceStatus('ready');
          setIsListening(false);
        };
      }
    }
  }, [handleVoiceCommand]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Error starting speech recognition:', e);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const resetMedications = () => {
    setMedicationsTaken([]);
    setCurrentMedIndex(0);
  };

  const handlePrimaryNavigate = (screen) => {
    setPrimaryScreen(screen);
  };

  const handleSupportNavigate = (screen) => {
    setSupportScreen(screen);
  };

  const renderPrimaryContent = () => {
    switch (primaryScreen) {
      case 'ambient':
        return (
          <PrimaryAmbient 
            onNavigate={handlePrimaryNavigate} 
            theme={primaryTheme}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
            medications={medications}
            medicationLogs={medicationLogs}
          />
        );
      case 'home':
        return (
          <PrimaryHome
            userName={userName}
            medications={medications}
            medicationsLoading={medicationsLoading}
            currentMedIndex={currentMedIndex}
            isListening={isListening}
            voiceStatus={voiceStatus}
            transcript={transcript}
            onNavigate={handlePrimaryNavigate}
            startListening={startListening}
            stopListening={stopListening}
            theme={primaryTheme}
            medicationLogs={medicationLogs}
            onMedicationTaken={handleMedicationTaken}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
          />
        );
      case 'cant-take':
        return (
          <PrimaryCantTakeMedication
            onNavigate={handlePrimaryNavigate}
            speak={speak}
            triggerSync={triggerSync}
            theme={primaryTheme}
          />
        );
      case 'cant-find':
        if (medicationsLoading || medications.length === 0) {
          return (
            <div className="min-h-screen flex items-center justify-center">
              <p className="text-2xl">Loading medications...</p>
            </div>
          );
        }
        return (
          <PrimaryCantFindMedication
            medications={medications}
            currentMedIndex={currentMedIndex}
            onNavigate={handlePrimaryNavigate}
            speak={speak}
            triggerSync={triggerSync}
            theme={primaryTheme}
          />
        );
      case 'medication-list':
        return (
          <PrimaryMedicationList
            userName={userName}
            medications={medications}
            medicationsTaken={medicationsTaken}
            onNavigate={handlePrimaryNavigate}
            theme={primaryTheme}
            medicationLogs={medicationLogs}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
          />
        );
      case 'help':
        return (
          <PrimaryHelp 
            userName={userName} 
            onNavigate={handlePrimaryNavigate} 
            theme={primaryTheme}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
          />
        );
      case 'success':
        return (
          <PrimarySuccess
            medications={medications}
            medicationsTaken={medicationsTaken}
            onNavigate={handlePrimaryNavigate}
            onReset={resetMedications}
            theme={primaryTheme}
            medicationLogs={medicationLogs}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
          />
        );
      default:
        return (
          <PrimaryAmbient 
            onNavigate={handlePrimaryNavigate} 
            theme={primaryTheme}
            debugTimeOverride={debugTimeOverride}
            debugTime={debugTime}
            medications={medications}
            medicationLogs={medicationLogs}
          />
        );
    }
  };

  const renderSupportContent = () => {
    switch (supportScreen) {
      case 'landing':
        return <SupportLanding onNavigate={handleSupportNavigate} />;
      case 'home':
        return <SupportHome onNavigate={handleSupportNavigate} medications={medications} />;
      case 'alert-detail':
        return <SupportAlertDetail onNavigate={handleSupportNavigate} medications={medications} />;
      case 'trends':
        return <SupportTrends onNavigate={handleSupportNavigate} />;
      case 'meds':
        return <SupportMeds onNavigate={handleSupportNavigate} />;
      case 'more':
        return <SupportMore onNavigate={handleSupportNavigate} />;
      default:
        return <SupportLanding onNavigate={handleSupportNavigate} />;
    }
  };

  return (
    <div>
      {/* Sync Indicator */}
      {showSync && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-2xl shadow-2xl flex items-center animate-bounce">
          <div className="w-3 h-3 bg-white rounded-full mr-3 animate-pulse"></div>
          <span className="font-semibold">⚡ Syncing: {lastSyncAction}</span>
          <ChevronRight className="w-5 h-5 ml-3" />
        </div>
      )}

      {/* Debug Mode Toggle - Show in both dev and production */}
      <ModeToggle 
        currentMode={userRole} 
        onModeChange={setUserRole} 
        onReset={resetTodayLogs}
        onTimeOverrideToggle={handleTimeOverrideToggle}
        debugTimeOverride={debugTimeOverride}
      />

      {/* Render Content based on user role */}
      {userRole === 'primary' ? renderPrimaryContent() : renderSupportContent()}
    </div>
  );
};

export default App;

